<template>
  <v-card outlined class="ma-2 overflow-hidden">
    <div :class="['hall-wrapper', isDark ? 'dark-mode' : 'light-mode']">
      <div class="info-card">
        <h3>{{ seatData.hallName }}</h3>

        <div class="details-grid">
          <div class="detail-item">
            <span class="label">{{ $t("seat.seat") }}</span>
            <span class="value accent-text">{{ seatData.seatLabel }}</span>
          </div>

          <div class="detail-item">
            <span class="label">{{ $t("seat.row") }}</span>
            <span class="value">{{ seatData.row }}</span>
          </div>

          <div class="detail-item">
            <span class="label">{{ $t("seat.column") }}</span>
            <span class="value">{{ seatData.column }}</span>
          </div>
        </div>
      </div>

      <div class="hall-layout">
        <div class="stage">
          {{ $t("seat.stageBoard") }}
        </div>
        <div :class="['door-indicator', seatData.doorDirection === 1 ? 'right' : 'left']">
          <span class="door-icon">🚪</span>
          {{ $t("seat.entrance") }}
        </div>

        <div class="scroll-wrapper">
          <div class="grid-container">
            <div
              class="seats-grid"
              :style="{
                gridTemplateColumns: `repeat(${seatData.columns}, 1fr)`,
                minWidth: `${seatData.columns * 55}px`
              }"
            >
              <template v-for="r in seatData.rows">
                <div
                  v-for="c in seatData.columns"
                  :key="r + '-' + c"
                  :class="[
                    'seat-box',
                    {
                      'is-target': r === seatData.row && c === seatData.column
                    }
                  ]"
                >
                  <template v-if="r === seatData.row && c === seatData.column">
                    <div class="my-seat-content">
                      <span class="user-icon">👤</span>
                      <small>{{ $t("seat.you") }}</small>
                    </div>
                  </template>

                  <template v-else>
                    <span class="seat-num"> {{ r }}-{{ c }} </span>
                  </template>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div class="map-legend">
        <div class="legend-dot target"></div>
        <span>{{ $t("seat.yourSeat") }}</span>

        <div class="legend-dot normal"></div>
        <span>{{ $t("seat.hallSeats") }}</span>
      </div>
    </div>
  </v-card>
</template>

<script>
export default {
  props: ["seatData"],
  computed: {
    isDark() {
      return this.$vuetify.theme.dark;
    },
    lang() {
      return this.$i18n.locale;
    }
  }
};
</script>

<style scoped>
/* تطبيق الألوان التي زودتني بها */
.light-mode {
  --bg-color: #f3f4f6; /* highlight */
  --card-bg: #ffffff;
  --text-main: #34495e;
  --seat-bg: #e5e7eb; /* grey-2 */
  --border-color: #d1d5db; /* grey-3 */
  --info-box: #e3f2fd; /* info */
  --grey-text: #9ca3af; /* grey-4 */
}

.dark-mode {
  --bg-color: #23272f; /* highlight / grey-1 */
  --card-bg: #2d323b; /* highlight-1 / grey-2 */
  --text-main: #f3f4f6;
  --seat-bg: #3a3f4b; /* grey-3 */
  --border-color: #4b5563; /* grey-4 */
  --info-box: #263238; /* info dark */
  --grey-text: #6b7280; /* grey-5 */
}

.hall-wrapper {
  direction: rtl;
  padding: 20px;
  border-radius: 12px;
  transition: all 0.3s ease;
  color: var(--text-main);
  background-color: var(--bg-color);
}

.info-card {
  background: var(--card-bg);
  padding: 10px;
  border-radius: 15px;
  margin-bottom: 20px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.details-grid {
  display: flex;
  justify-content: space-around;
  margin-top: 15px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  background: var(--info-box);
  padding: 10px 25px;
  border-radius: 10px;
  border: 1px solid var(--border-color);
}

.accent-text {
  color: #109afd; /* primary */
  font-weight: bold;
}

.hall-layout {
  background: var(--card-bg);
  border: 2px solid var(--border-color);
  padding: 10px 5px;
  border-radius: 10px;
  position: relative;
}

.stage {
  width: 60%;
  margin: 0 auto 20px;
  background: #6b7280; /* grey-5 */
  color: white;
  text-align: center;
  padding: 8px;
  border-radius: 0 0 20px 20px;
  font-size: 0.9em;
}

/* حل مشكلة التمرير */
.scroll-wrapper {
  overflow-x: auto;
  padding: 30px 10px 10px;
  scrollbar-width: thin;
  scrollbar-color: #109afd transparent;
}

/* تنسيق السكرول لمتصفحات Chrome */
.scroll-wrapper::-webkit-scrollbar {
  height: 5px;
}
.scroll-wrapper::-webkit-scrollbar-thumb {
  background: #109afd;
  border-radius: 10px;
}

.grid-container {
  position: relative;
  width: max-content;
  margin: 0 auto;
}

.seats-grid {
  display: grid;
  gap: 12px;
  justify-content: center;
}

.seat-box {
  width: 45px;
  height: 45px;
  background: var(--seat-bg);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7em;
  color: var(--grey-text);
}

.seat-box.is-target {
  background: #109afd !important; /* primary */
  color: white !important;
  transform: scale(1.15);
  box-shadow: 0 0 15px rgba(16, 154, 253, 0.4);
  border: 2px solid #ffffff;
  z-index: 2;
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%,
  100% {
    transform: scale(1.15);
  }
  50% {
    transform: scale(1.25);
  }
}

.door-indicator {
  top: -25px;
  font-size: 0.8em;
  font-weight: bold;
  color: #ec407a; /* secondary */
}
.door-indicator.right {
  text-align: right;
}
.door-indicator.left {
  text-align: left;
}

.map-legend {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  font-size: 0.9em;
}
.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}
.legend-dot.target {
  background: #109afd;
}
.legend-dot.normal {
  background: var(--seat-bg);
}
</style>
